import React from "react";
import { Layout, Menu, Avatar, Typography, Button } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetIdentity } from "@refinedev/core";
import { sectionVisible } from "@/services/privileges";
import { getUserFromToken } from "@/services/keycloak";
import {
  LayoutDashboard,
  Film,
  Users,
  CreditCard,
  BarChart3,
  Bell,
  User,
  Tag,
  Settings,
  Smartphone,
  MessageCircle,
  LogOut,
} from "lucide-react";
import "./Sidebar.css";

const { Sider } = Layout;
const { Text } = Typography;

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
}

const menuItems: MenuItem[] = [
  {
    key: "dashboard",
    icon: <LayoutDashboard size={20} />,
    label: "Dashboard",
    path: "/",
  },
  {
    key: "content",
    icon: <Film size={20} />,
    label: "Contenu",
    path: "/content",
  },
  {
    key: "users",
    icon: <Users size={20} />,
    label: "Utilisateurs",
    path: "/users",
  },
  {
    key: "subscriptions",
    icon: <CreditCard size={20} />,
    label: "Abonnements & Paiements",
    path: "/subscriptions",
  },
  {
    key: "statistics",
    icon: <BarChart3 size={20} />,
    label: "Statistiques",
    path: "/statistics",
  },
  {
    key: "notifications",
    icon: <Bell size={20} />,
    label: "Notifications",
    path: "/notifications",
  },
  {
    key: "artists",
    icon: <User size={20} />,
    label: "Artistes & Réalisateurs",
    path: "/artists",
  },
  {
    key: "categories",
    icon: <Tag size={20} />,
    label: "Catégories & Genres",
    path: "/categories",
  },
  {
    key: "types",
    icon: <Film size={20} />,
    label: "Types de Contenu",
    path: "/types",
  },
  {
    key: "account-limits",
    icon: <Smartphone size={20} />,
    label: "Comptes & appareils",
    path: "/account-limits",
  },
  {
    key: "technical-settings",
    icon: <Settings size={20} />,
    label: "Paramètres Techniques",
    path: "/technical-settings",
  },
  {
    key: "comment-moderation",
    icon: <MessageCircle size={20} />,
    label: "Modération des Commentaires",
    path: "/comment-moderation",
  },
  // « Tests A/B » retire du menu le 2026-09-27 (BO-09). L'analyse a conclu que
  // l'audience actuelle — 2 comptes abonnes, 1 spectateur — rend tout test
  // statistiquement vide : detecter +20 % de conversion exigerait 16 310
  // participants. Une entree qui n'aboutit a rien erode la confiance dans le
  // reste du backoffice. La page et sa route subsistent, inoffensives.
  // A rouvrir a 2 000 abonnes actifs par mois : voir docs/ANALYSE-TESTS-AB.md
];

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: identity } = useGetIdentity();

  // Les roles viennent du jeton, seule source fiable : ils sont emis par
  // Keycloak et c'est sur eux que l'API fonde ses refus. Une section pour
  // laquelle l'API repondrait 403 n'a pas a figurer dans le menu.
  const roles = React.useMemo(() => getUserFromToken()?.roles, []);
  const sectionsVisibles = React.useMemo(
    () => menuItems.filter((item) => sectionVisible(item.key, roles)),
    [roles]
  );

  const handleMenuClick = (item: MenuItem) => {
    navigate(item.path);
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const getSelectedKey = () => {
    const currentPath = location.pathname;
    const menuItem = menuItems.find(
      (item) =>
        item.path === currentPath ||
        (item.path !== "/" && currentPath.startsWith(item.path))
    );
    return menuItem ? [menuItem.key] : ["dashboard"];
  };

  return (
    <Sider width={280} className="sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <img src="/filmika-logo.png" alt="Filmika" className="logo-img" />
          <Text strong className="logo-text">
            Backoffice
          </Text>
        </div>
      </div>

      <div className="sidebar-content">
        <Menu
          mode="inline"
          selectedKeys={getSelectedKey()}
          className="sidebar-menu"
          items={sectionsVisibles.map((item) => ({
            key: item.key,
            icon: item.icon,
            label: item.label,
            onClick: () => handleMenuClick(item),
            className:
              location.pathname === item.path
                ? "menu-item-active"
                : "menu-item",
          }))}
        />
      </div>

      <div className="sidebar-footer">
        <div className="user-profile">
          <Avatar
            size={40}
            src={identity?.profileImageUrl}
            className="user-avatar"
          >
            {identity?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <div className="user-info">
            <Text strong className="user-name">
              {identity?.username || "Administrateur"}
            </Text>
            <Text type="secondary" className="user-role">
              Administrateur
            </Text>
          </div>
          <Button
            type="text"
            icon={<LogOut size={16} />}
            onClick={handleLogout}
            className="logout-button"
          />
        </div>
      </div>
    </Sider>
  );
};
